import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import axios from 'axios';
import { Loader2Icon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import {
  getV2ParentPaymentMethodSetup,
  postParentPaymentMethodSetupFinalize,
  postV2ParentPaymentMethod,
  removeParentPaymentMethodSetupQuery,
  useParentPaymentMethodSetupQuery,
} from '~entities/stripe/api/parent-payment-method-setup';
import { useGetStudioDefaultPaymentProvider } from '~entities/studios/api/get-studio-default-payment-provider';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { KEYS } from '~shared/api/query-keys';
import { SquareSaveParentCardForm } from '~shared/components/square-save-parent-card-form';
import { isSquarePaymentProvider } from '~shared/lib/square-web-payments';
import { reportError } from '~shared/observability/report-error';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { getErrorMessage } from '~shared/utils/error-handler';

interface StripeSetupConfig {
  clientSecret: string;
  publishableKey: string;
  stripeAccount?: string;
  linkDisabled?: boolean;
}

/** Survives full-page return from Stripe (e.g. 3DS) so we can call finalize. */
const PENDING_PARENT_PAYMENT_SETUP_PARENT_ID =
  'enrollio:pendingParentPaymentSetupParentId';

function stripStripeSetupIntentReturnParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete('setup_intent');
  url.searchParams.delete('setup_intent_client_secret');
  url.searchParams.delete('redirect_status');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(null, '', next);
}

/** Ensures only one finalize runs when multiple dialog instances mount (e.g. nested routes). */
let resumeParentPaymentSetupPromise: Promise<void> | null = null;

const SavePaymentForm = ({
  parentId,
  onClose,
  onSuccess,
  disableLink,
  saveSetup,
}: {
  parentId: string;
  onClose: () => void;
  onSuccess: () => void;
  disableLink?: boolean;
  saveSetup: (setupIntentId: string) => Promise<unknown>;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    sessionStorage.setItem(PENDING_PARENT_PAYMENT_SETUP_PARENT_ID, parentId);
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        sessionStorage.removeItem(PENDING_PARENT_PAYMENT_SETUP_PARENT_ID);
        // card_error / validation_error are expected user issues and stay
        // toast-only; anything else is a real Stripe/integration failure.
        if (error.type !== 'card_error' && error.type !== 'validation_error') {
          reportError(error, {
            featureArea: 'payment_method_setup',
            parentId,
            errorCode: error.type,
          });
        }
        toast.error(error.message ?? 'Could not save payment method');
        return;
      }

      const setupIntentId = setupIntent.id;
      if (typeof setupIntentId !== 'string') {
        sessionStorage.removeItem(PENDING_PARENT_PAYMENT_SETUP_PARENT_ID);
        reportError(
          new Error('Stripe confirmSetup returned no setupIntent id'),
          {
            featureArea: 'payment_method_setup',
            parentId,
            errorCode: 'missing_setup_intent_id',
          },
        );
        toast.error('Missing setup confirmation. Please try again.');
        return;
      }

      await saveSetup(setupIntentId);

      sessionStorage.removeItem(PENDING_PARENT_PAYMENT_SETUP_PARENT_ID);
      toast.success('Payment method saved');
      onSuccess();
    } catch (err) {
      sessionStorage.removeItem(PENDING_PARENT_PAYMENT_SETUP_PARENT_ID);
      console.error('save-parent-payment-method:', err);
      // Axios failures (the finalize call) are captured by the response
      // interceptor; only report client-side (Stripe SDK) throws here.
      if (!axios.isAxiosError(err)) {
        reportError(err, { featureArea: 'payment_method_setup', parentId });
      }
      toast.error(getErrorMessage(err, 'Failed to save payment method'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
      className='space-y-4'
    >
      <PaymentElement
        options={
          disableLink
            ? ({ wallets: { link: 'never' } } as Record<string, unknown>)
            : undefined
        }
      />
      <DialogFooter className='gap-2 sm:gap-0'>
        <Button type='button' variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button type='submit' disabled={isSubmitting || !stripe}>
          {isSubmitting ? (
            <Loader2Icon className='size-4 animate-spin' />
          ) : (
            'Save payment method'
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

export interface SaveParentPaymentMethodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId: string;
  /** Shown for context only (e.g. which family is being updated). */
  parentEmail?: string | null;
  onSuccess?: () => void;
}

export const SaveParentPaymentMethodDialog = ({
  open,
  onOpenChange,
  parentId,
  parentEmail,
  onSuccess,
}: SaveParentPaymentMethodDialogProps) => {
  const [stripeConfig, setStripeConfig] = useState<StripeSetupConfig | null>(
    null,
  );
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const {
    data: providerData,
    isSuccess: providerReady,
    isError: providerFailed,
    error: providerError,
    refetch: refetchProvider,
  } = useGetStudioDefaultPaymentProvider();
  const { data: studioDetails, isSuccess: studioReady } = useGetStudioDetails();
  const isSquare = isSquarePaymentProvider(providerData?.paymentProvider);
  const isV2 = studioDetails?.isV2 === true;
  const stripeSetupReady = open && providerReady && studioReady && !isSquare;

  const legacySetupQuery = useParentPaymentMethodSetupQuery(
    parentId,
    stripeSetupReady && !isV2,
  );
  const v2SetupQuery = useQuery({
    queryKey: [KEYS.PARENT_PAYMENT_METHOD_SETUP, 'v2', parentId],
    queryFn: () => getV2ParentPaymentMethodSetup(parentId),
    enabled: stripeSetupReady && isV2,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
  const setupQuery = isV2 ? v2SetupQuery : legacySetupQuery;

  const saveSetup = (setupIntentId: string) =>
    isV2
      ? postV2ParentPaymentMethod({
          parentId,
          clientSetupId: setupIntentId,
        })
      : postParentPaymentMethodSetupFinalize({
          parentId,
          setupIntentId,
        });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setupIntentId = params.get('setup_intent');
    const redirectStatus = params.get('redirect_status');
    const pendingParentId = sessionStorage.getItem(
      PENDING_PARENT_PAYMENT_SETUP_PARENT_ID,
    );

    if (!setupIntentId || !pendingParentId) return;

    if (redirectStatus === 'failed') {
      sessionStorage.removeItem(PENDING_PARENT_PAYMENT_SETUP_PARENT_ID);
      stripStripeSetupIntentReturnParams();
      toast.error('Card verification failed. Please try again.');
      return;
    }

    if (redirectStatus !== 'succeeded') {
      return;
    }

    if (resumeParentPaymentSetupPromise) {
      return;
    }

    sessionStorage.removeItem(PENDING_PARENT_PAYMENT_SETUP_PARENT_ID);
    stripStripeSetupIntentReturnParams();

    resumeParentPaymentSetupPromise = (async () => {
      try {
        if (isV2) {
          await postV2ParentPaymentMethod({
            parentId: pendingParentId,
            clientSetupId: setupIntentId,
          });
        } else {
          await postParentPaymentMethodSetupFinalize({
            parentId: pendingParentId,
            setupIntentId,
          });
        }
        void queryClient.invalidateQueries({ queryKey: [KEYS.CARD] });
        toast.success('Payment method saved');
        onSuccess?.();
      } catch (err) {
        console.error('save-parent-payment-method redirect resume:', err);
        toast.error(
          getErrorMessage(
            err,
            'Failed to save payment method after verification',
          ),
        );
      } finally {
        resumeParentPaymentSetupPromise = null;
      }
    })();
  }, [onSuccess, isV2]);

  // Clear only after `open` is false so the setup query is already disabled
  // and removeQueries cannot trigger a throwaway SetupIntent refetch.
  useEffect(() => {
    if (!open) {
      setStripeConfig(null);
      setStripeInstance(null);
      removeParentPaymentMethodSetupQuery(parentId);
    }
  }, [open, parentId]);

  useEffect(() => {
    if (!open) return;
    // Ignore stale cached success while a fresh SetupIntent is in flight.
    if (setupQuery.isFetching || !setupQuery.isSuccess || !setupQuery.data) {
      return;
    }
    const d = setupQuery.data;
    const clientSecret = d.clientSecret;
    const publishableKey =
      'publishableKey' in d && d.publishableKey
        ? d.publishableKey
        : 'stripePublicApiKey' in d
          ? d.stripePublicApiKey
          : undefined;
    const stripeAccount =
      'connectedAccountId' in d && d.connectedAccountId
        ? d.connectedAccountId
        : 'stripeAccount' in d
          ? d.stripeAccount
          : undefined;
    if (!clientSecret || !publishableKey) {
      return;
    }
    setStripeConfig((prev) => {
      if (prev?.clientSecret === clientSecret) {
        return prev;
      }
      return {
        clientSecret,
        publishableKey,
        stripeAccount,
        linkDisabled: d.linkDisabled,
      };
    });
  }, [
    open,
    setupQuery.isFetching,
    setupQuery.isSuccess,
    setupQuery.data,
  ]);

  useEffect(() => {
    if (setupQuery.isError && open) {
      const message =
        setupQuery.error instanceof Error
          ? setupQuery.error.message
          : 'Could not start payment setup';
      toast.error(message);
      onOpenChange(false);
    }
  }, [setupQuery.isError, setupQuery.error, open, onOpenChange]);

  useEffect(() => {
    let cancelled = false;
    const initStripe = async () => {
      if (!open || !stripeConfig?.publishableKey) return;
      const stripe = await loadStripe(
        stripeConfig.publishableKey,
        stripeConfig.stripeAccount
          ? { stripeAccount: stripeConfig.stripeAccount }
          : undefined,
      );
      if (cancelled) return;
      setStripeInstance(stripe);
      if (!stripe) {
        reportError(
          new Error('Stripe.js loaded a null instance (check publishableKey)'),
          {
            featureArea: 'payment_method_setup',
            parentId,
            errorCode: 'null_stripe_instance',
          },
        );
        toast.error('Failed to load the payment form');
      }
    };
    void initStripe();
    return () => {
      cancelled = true;
    };
  }, [open, stripeConfig?.publishableKey, stripeConfig?.stripeAccount]);

  const handleClose = (next: boolean) => {
    onOpenChange(next);
  };

  const handleSuccess = () => {
    onSuccess?.();
    handleClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle>Save payment method for parent</DialogTitle>
          <DialogDescription>
            Only use when the parent has authorized your studio to add or update
            their card on file. Card data is sent securely to{' '}
            {providerReady
              ? isSquare
                ? 'Square'
                : 'Stripe'
              : 'your payment processor'}
            ; the Studio Portal does not store card numbers.
            {parentEmail ? (
              <>
                {' '}
                <span className='font-medium text-foreground'>
                  {parentEmail}
                </span>
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {providerFailed ? (
          <div className='space-y-4 py-4 text-center'>
            <p className='text-sm font-medium'>
              {getErrorMessage(
                providerError,
                'Could not load the studio payment provider',
              )}
            </p>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  handleClose(false);
                }}
              >
                Cancel
              </Button>
              <Button type='button' onClick={() => void refetchProvider()}>
                Try again
              </Button>
            </DialogFooter>
          </div>
        ) : !providerReady ? (
          <div className='flex items-center justify-center gap-2 py-8 text-muted-foreground'>
            <Loader2Icon className='size-6 animate-spin' />
            <span>Preparing secure form…</span>
          </div>
        ) : isSquare ? (
          <SquareSaveParentCardForm
            parentId={parentId}
            parentEmail={parentEmail}
            onClose={() => {
              handleClose(false);
            }}
            onSuccess={handleSuccess}
          />
        ) : setupQuery.isPending ||
          setupQuery.isFetching ||
          !stripeConfig ? (
          <div className='flex items-center justify-center gap-2 py-8 text-muted-foreground'>
            <Loader2Icon className='size-6 animate-spin' />
            <span>Preparing secure form…</span>
          </div>
        ) : stripeInstance ? (
          <Elements
            key={stripeConfig.clientSecret}
            stripe={stripeInstance}
            options={{ clientSecret: stripeConfig.clientSecret }}
          >
            <SavePaymentForm
              parentId={parentId}
              onClose={() => {
                handleClose(false);
              }}
              onSuccess={handleSuccess}
              disableLink={stripeConfig.linkDisabled}
              saveSetup={saveSetup}
            />
          </Elements>
        ) : (
          <div className='py-6 text-center text-sm text-muted-foreground'>
            Initializing payment form…
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
